/**
 * How far this device's learner has got — or how far the preview says they have.
 *
 * Two callers draw a run on a card rather than in the player — the card in a feed
 * and the module's own page — and both need the same answer, so they come through
 * here: the device's own run (`#/state/moduleRuns`) with a dev preview allowed to
 * stand in for it.
 *
 * The preview *reads*; it never writes. A run is the learner's own work on this
 * device, and a preview that seeded one would have to clean up after itself — and
 * would eventually fail to. So the pretended run is turned into the same `done`
 * list the store would have held, and handed to the same pure rules
 * (`moduleRunProgress`), which is why a case cannot show a progress the app could
 * not otherwise reach.
 */
import {useMemo} from 'react'

import {modulePreviewRunDone, readModulePreviewRun} from '#/lib/moduleFixtures'
import {moduleRunProgress, type ModuleRunProgress} from '#/lib/moduleRun'
import {useModuleRun} from '#/state/moduleRuns'
import {IS_DEV} from '#/env'
import {useModulePreviewCase} from './useModulePreviewCase'

export function useModuleRunWithPreview({
  moduleUri,
  items,
}: {
  moduleUri: string | undefined
  items: readonly {uri: string}[]
}): {
  /** The capsules of this playlist whose questions are finished. */
  done: string[]
  progress: ModuleRunProgress
} {
  /* Subscribed, so choosing a run case redraws the card that is drawing it. */
  useModulePreviewCase()
  const stored = useModuleRun({moduleUri, items})
  const run = IS_DEV ? readModulePreviewRun() : null

  return useMemo(() => {
    if (!run) return {done: stored.done, progress: stored.progress}
    const done = modulePreviewRunDone(items, run)
    return {done, progress: moduleRunProgress({items, done})}
  }, [items, run, stored.done, stored.progress])
}
